import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SearchService } from '../search.service';
import { SearchContext } from '../shared/search-context';
import { SwirlService } from '../swirl.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../auth/auth.service';

const RESULT_MIXER_KEY = 'result_mixer';
const DEFAULT_MIXER = 'result_mixer:RelevancyConfidenceMixer';
type DropItem =
    | { kind: 'source'; key: string; label: string; src: { value: string; name: string; connector: string; selected: boolean; tags: string[] } }
    | { kind: 'tag';    key: string; label: string; tagValue: string };

@Component({
  selector: 'app-box',
  templateUrl: './box.component.html',
  styleUrls: ['./box.component.scss'],
  standalone: false,
})
export class BoxComponent implements OnInit, OnDestroy {
  @Input() promptsExists: boolean = false;

  searchForm: UntypedFormGroup;
  searchContext: SearchContext = new SearchContext();

  allSourcesState: 'checked' | 'indeterminate' | 'unchecked' = 'checked';
  sources: { value: string; name: string; connector: string; selected: boolean; tags: string[] }[] = [
    { value: 'default', name: 'All Sources', connector: 'default', selected: true, tags: [] }
  ];

  spTags: { value: string; name: string; count: number }[] = [];
  private tagToSourceIdx = new Map<string, number[]>(); // tag -> array of source indices
  tagStates = new Map<string, 'checked' | 'indeterminate' | 'unchecked'>();

  dropdownItems: DropItem[] = [];
  trackByDropItem = (_: number, item: DropItem) => item.key;

  selectedSources: FormControl<string[]> = new FormControl<string[]>(['default'], { nonNullable: true });
  previousSelectedSources: string[] = ['default'];

  // Initialize prompts array without the 'reset' entry
  prompts: { value: string; name: string }[] = [];

  hasPrompts: boolean = false;

  currentRagPrompt: string = '';

  currentAiInstructions: string = '';

  dataReady: boolean = false;

  hasResults: boolean = false;

  previousParams: { q: string, providers: string, rag: string, rag_timeout: string, prompt: string } = {
    q: '',
    providers: '',
    rag: '',
    rag_timeout: '',
    prompt: ''
  };

  checkedGenerateAiResponse: boolean = false;
  isRAGButtonDisabled: boolean = false;

  searchButtonLabel: string = 'SEARCH';
  searchBarPlaceholder: string = 'What are you searching for today?';
  aiResponseLabel: string = 'Generate AI Response';
  aiInstructionsPlaceholder: string = 'Optional instructions for the AI Response...';

  isLoading: boolean = false; // For Search operations
  isRagLoading: boolean = false; // For RAG operations

  // Variable to store the default prompt value
  defaultPromptValue: string = '';

  // AI Provider status tracking
  isAIProviderExists: boolean = true;
  isAIProviderLoaded: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private searchService: SearchService,
    private swirl: SwirlService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {
    this.searchForm = this.fb.group({
      query: [{ value: '', disabled: this.isLoading }], // Initially disabled if isLoading is true
      checkedGenerateAiResponse: [false],
      selectedSources: this.selectedSources,
      selectedPrompt: [''], // Initialize without default value
      ai_instructions: ['']
    });
  }

  get isSearchButtonDisabled(): boolean {
    return this.searchForm.get('query')?.value?.trim() === '';
  }

  ngOnInit() {
    // Subscribe to AIProvider status; update loaded flag once done
    this.swirl.getIsAIProviderExistsStatus().subscribe((data) => {
      this.isAIProviderLoaded = true; // provider check complete
      if (data && data.status) {
        const exists = data.status === 'True';
        this.authService.setAIProviderStatus(exists);
        this.isAIProviderExists = exists;
      } else {
        this.isAIProviderExists = false;
      }
    });

    this.swirl.getSources().subscribe(async response => {
      response
        .filter((source: any) => source.active)
        .sort((a: any, b: any) => a.name.localeCompare(b.name))
        .forEach((source: any) => {
          this.sources.push({
            value: source.id.toString(),
            name: source.name,
            connector: source.connector,
            selected: false,
            tags: source.tags
          });
        });

      this.tagToSourceIdx.clear();
      const tagMap = new Map<string, { name: string; count: number }>();

      for (let i = 0; i < this.sources.length; i++) {
        const src = this.sources[i];
        const tags = Array.isArray(src.tags) ? src.tags : [];
        for (const tag of tags) {
          const name = tag.trim();
          if (!name) continue;
          const key = name.toLowerCase();
          // skip tags that contain ':' (e.g. SINGLEPROVIDER:20)
          if (key.includes(':')) continue;

          if (!this.tagToSourceIdx.has(key)) this.tagToSourceIdx.set(key, []);
          this.tagToSourceIdx.get(key)!.push(i);

          const entry = tagMap.get(key);
          if (!entry) tagMap.set(key, { name, count: 1 });
          else entry.count++;
        }
      }

      this.spTags = Array.from(tagMap)
          .filter(([_, v]) => v.count > 1)
          .map(([value, v]) => ({ value, name: v.name, count: v.count }))
          .sort((a, b) => a.name.localeCompare(b.name));

      this.syncSelectedSources();

      if (this.authService.getPromptsStatus()) { // Only fetch prompts if they are supported
        this.swirl.getPrompts().subscribe(response => {
          const searchRagDefaultPrompt = response.find((prompt: any) => prompt.name === 'search_rag_deeplink' && prompt.active);
          const chatRagDefaultPrompt = response.find((prompt: any) => prompt.name === 'chat_rag_deeplink' && prompt.active);

          if (!searchRagDefaultPrompt) console.error('Default prompt "search_rag_deeplink" not found.');
          if (!chatRagDefaultPrompt) console.error('Default prompt "chat_rag_deeplink" not found.');

          // Add "Search RAG Deeplink" as the first prompt
          if (searchRagDefaultPrompt) {
            this.prompts.push({
              value: searchRagDefaultPrompt.id.toString(),
              name: 'Search Rag Deeplink'
            });
            this.defaultPromptValue = searchRagDefaultPrompt.id.toString(); // Set as default
          }

          // Add "Chat Rag Deeplink" as the second prompt
          if (chatRagDefaultPrompt) {
            this.prompts.push({
              value: chatRagDefaultPrompt.id.toString(),
              name: 'Chat Rag Deeplink'
            });
          }

          // Add other prompts with 'rag' tag, excluding the default ones
          const filteredPrompts = response.filter(
            (prompt: any) =>
              prompt.active &&
              prompt.tags.some((tag: any) => tag.includes('rag')) &&
              prompt.name !== 'search_rag_deeplink' &&
              prompt.name !== 'chat_rag_deeplink'
          );

          filteredPrompts.forEach((prompt: any) => {
            this.prompts.push({
              value: prompt.id.toString(),
              name: this.formatPromptName(prompt.name),
            });
          });

          // Determine if prompts exist and update both local and input-bound flags.
          this.hasPrompts = this.prompts.length > 0;
          this.promptsExists = this.hasPrompts; // Synchronize the input with actual data

          // Once sources and prompts are ready
          this.dataReady = true;

          // Conditional default prompt setting
          const currentSelectedPrompt = this.searchForm.get('selectedPrompt')?.value;
          if (!currentSelectedPrompt && this.defaultPromptValue) {
            this.searchForm.get('selectedPrompt')?.setValue(this.defaultPromptValue);
          }
        });
      } else {
        // If no prompts are needed, update both flags accordingly.
        this.hasPrompts = false;
        this.promptsExists = false;
        this.dataReady = true;
      }

      // Subscribe to current search context
      this.searchService.currentSearchContext.subscribe(searchContext => {
        this.searchContext = searchContext;
        // Reset the loading state since search is complete
        this.setSearchLoadingState(false);
      });

      // Handle query parameters for search
      this.route.queryParamMap.subscribe(params => {
        const state = this.router.currentNavigation()?.extras.state;
        const q = params.get('q');
        const providers = params.get('providers');
        const rag = params.get('rag');
        const rag_timeout = params.get('rag_timeout');
        let promptParam = params.get('prompt');
        let aiInstructionsParam = params.get('ai_instructions');

        // Update the form control with the persisted instructions:
        if (aiInstructionsParam) {
          this.searchForm.get('ai_instructions')?.setValue(aiInstructionsParam);
          this.currentAiInstructions = aiInstructionsParam;
        }

        const paramsObject = { q, providers, rag, rag_timeout, prompt: promptParam };

        if (state?.rerun) {
          this.previousParams = {
            q: q || '',
            providers: providers || '',
            rag: rag || '',
            rag_timeout: rag_timeout || '',
            prompt: promptParam || ''
          };
          this.makeSearch(paramsObject);
          this.router.navigate(['/'], { queryParams: paramsObject });
        } else {
          if (!this.compareObjects(paramsObject, this.previousParams))
            this.makeSearch(paramsObject);
        }
      });

      // Handle erase event
      this.searchService.eraseEvent.subscribe((isErased) => {
        if (isErased) {
          this.router.navigate(['/']);
          this.searchService.changeEraseEvent(false);
          this.searchForm.get('checkedGenerateAiResponse')?.patchValue(false);
          this.searchForm.get('selectedPrompt')?.setValue(this.defaultPromptValue);
          // this.isSearchButtonDisabled = false;
          this.isRAGButtonDisabled = false;
        }
      });

      this.searchService.currentResults.subscribe(results => {
        // Ensure results.docs is an array; if not, default to false.
        this.hasResults = results && results.docs && results.docs.length > 0;
        this.updateAiInstructionsState();  // Update the AI instructions control state when search results change
      });
    });

    // Fetch branding values
    this.swirl.getBrandingValues('config').subscribe((values) => {
      this.searchButtonLabel = values.search_button_label ?? this.searchButtonLabel;
      this.searchBarPlaceholder = values.search_bar_placeholder ?? this.searchBarPlaceholder;
      this.aiResponseLabel = values.ai_response_label ?? this.aiResponseLabel;
      this.aiInstructionsPlaceholder = values.ai_instructions_placeholder ?? this.aiInstructionsPlaceholder;
    });

    // Subscribe to search loading state
    this.searchService.isLoading.subscribe((isLoading) => {
      this.setSearchLoadingState(isLoading);
      this.updateAiInstructionsState(); // Update the AI instructions control state separately
      const checkedGenerateAiResponseControl = this.searchForm.get('checkedGenerateAiResponse');
      if (isLoading && checkedGenerateAiResponseControl) {
        checkedGenerateAiResponseControl.disable();
      } else if (checkedGenerateAiResponseControl) {
        checkedGenerateAiResponseControl.enable();
      }
    });

    // Subscribe to RAG loading state
    this.searchService.isRagLoading.subscribe((isRagLoading) => {
      this.setRagLoadingState(isRagLoading);
      this.searchForm.get('checkedGenerateAiResponse')?.patchValue(isRagLoading);
    });

  }

  ngOnDestroy() {
    // No subscriptions to clean up related to ResetService
  }

  /**
   * Getter to determine the display name of the selected prompt.
   * Displays the actual prompt name.
   */
  get selectedPromptName(): string {
    const selectedValue = this.searchForm.get('selectedPrompt')?.value;
    const selected = this.prompts.find(p => p.value === selectedValue);
    return selected ? selected.name : 'Select Prompt';
  }

  /**
   * Helper method to get the effective prompt value.
   * Returns the selected prompt's value.
   */
  private getEffectivePromptValue(): string | undefined {
    const selectedPromptValue = this.searchForm.get('selectedPrompt')?.value;
    return selectedPromptValue;
  }

  /**
   * Manages the search loading state by enabling/disabling the query control.
   */
  setSearchLoadingState(isLoading: boolean): void {
    this.isLoading = isLoading;
  
    const queryControl = this.searchForm.get('query');
    if (isLoading) {
      queryControl?.disable(); // Disable the control when loading
    } else {
      queryControl?.enable(); // Enable the control when not loading
    }
  }

  /**
   * Manages the RAG loading state.
   */
  setRagLoadingState(isLoading: boolean): void {
    this.isRagLoading = isLoading;
  }

  // Helper method for managing state of the AI Instructions input
  updateAiInstructionsState(): void {
    const instructionsControl = this.searchForm.get('ai_instructions');
    instructionsControl?.enable();
  }

  /**
   * Handles prompt selection from the dropdown.
   */
  selectPrompt(value: string): void {
    if (!this.isAIProviderExists) {
      return;
    }

    this.searchForm.get('selectedPrompt')?.setValue(value);
    const effectivePrompt = this.getEffectivePromptValue();

    const queryParams = { ...this.route.snapshot.queryParams };
    if (effectivePrompt) {
      queryParams['prompt'] = effectivePrompt;
    } else {
      delete queryParams['prompt'];
    }

    this.router.navigate([], { queryParams });
  }

  /**
   * Getter for the checkedGenerateAiResponse control.
   */
  get checkedGenerateAiResponseControl(): FormControl | null {
    const control = this.searchForm.get('checkedGenerateAiResponse');
    if (control instanceof FormControl) {
      return control;
    }
    console.error('Error: checkedGenerateAiResponse is not a FormControl');
    return null;
  }

  /**
   * Returns the concatenated names of selected sources.
   */
  get selectedSourcesNames(): string {
    if (this.selectedSources.value.includes('default') || this.selectedSources.value.length === 0) {
      return 'All Sources';
    }
    
    const selectedSources = this.sources
      .filter((source) => source.selected)
      .map((source) => source.name);
    return selectedSources.length > 0 ? selectedSources.join(', ') : 'All Sources';
  }

  /**
   * Initiates the search based on provided parameters.
   */
  makeSearch(params: any) {
    const q = params['q'];
    if (q) {
      // **Set the 'query' form control immediately**
      this.searchForm.get('query')?.setValue(q);
      
      const rag = params['rag'];
      const rawPrompt = params['prompt'];
      
      if (this.isAIProviderExists) {
        this.searchForm.get('selectedPrompt')?.setValue(rawPrompt || this.defaultPromptValue);
        let prompt = this.getEffectivePromptValue();
      }

      // The display is handled by the getter

      const providers = params['providers'];
      let parsedProviders: string[] = [];
      if (providers?.includes(',')) {
        parsedProviders = providers.split(',');
        this.selectedSources.setValue(parsedProviders.map((p: any) => p.toString()));
      } else if (providers) {
        this.selectedSources.setValue([providers.toString()]);
      } else {
        this.selectedSources.setValue(['default']);
      }

       // **Synchronize `this.sources` with `selectedSources`**
      this.syncSelectedSources();

      const sourceConnectorName = this.sources
        .filter(s => this.selectedSources.value.includes(s.value))
        .map(s => s?.connector) || [];
      const allSourceConnectorNames = this.sources.map(s => s.connector);
      const isDefault = this.selectedSources.value.length === 1 && this.selectedSources.value[0] === 'default';
      const sourceConnectorNames = [...new Set(isDefault ? allSourceConnectorNames : sourceConnectorName)];
      const theMixer = this.searchContext.filters.get(RESULT_MIXER_KEY) || DEFAULT_MIXER;
      this.searchService.putFilterWithoutSearch(RESULT_MIXER_KEY, theMixer);
      this.searchService.changeQueryAndProviders(q as string, isDefault ? [] : this.selectedSources.value, sourceConnectorNames);
    } 
    else {
      this.selectedSources.setValue(['default']);
      this.searchForm.get('query')?.patchValue('');

      // **Synchronize `this.sources` with `selectedSources`**
      this.syncSelectedSources();
    }

    setTimeout(() => {
      this.previousParams = {
        q: q || '',
        providers: params['providers'] || '',
        rag: params['rag'] || '',
        rag_timeout: params['rag_timeout'] || '',
        prompt: params['prompt'] || ''
      };
    }, 100);
  }

  /**
   * Synchronizes the 'selected' property of each source in 'this.sources' with the 'selectedSources' FormControl value.
   */
  private syncSelectedSources(): void {
    const selectedValues = this.selectedSources.value ?? [];
    const defaultOn = selectedValues.includes('default') || selectedValues.length === 0;

    this.sources.forEach(source => {
      if (source.value === 'default') {
        source.selected = defaultOn; // only shows as checked when defaultOn=true
      } else {
        source.selected = defaultOn ? true : selectedValues.includes(source.value);
      }
    });

    this.updateAllSourcesState();
    this.updateTagStates();
    this.rebuildDropdownItems();
  }
  
  /**
   * Compares two objects, excluding 'rag' and 'prompt' keys.
   */
  compareObjects(obj1: any, obj2: any) {
    for (let key in obj1) {
      if (key === 'rag' || key === 'prompt') continue;
      if ((obj1?.[key] || '') !== (obj2?.[key] || '')) {
        return false;
      }
    }
    for (let key in obj2) {
      if (key === 'rag' || key === 'prompt') continue;
      if ((obj1?.[key] || '') !== (obj2?.[key] || '')) {
        return false;
      }
    }
    return true;
  }

  /**
   * Handles toggling of source selections.
   */
  onSourceToggle(source: { value: string; name: string; connector: string; selected: boolean }): void {
    if (source.value === 'default') {
      const next = !source.selected;

      // Visually toggle everyone
      this.sources.forEach(s => (s.selected = next));

      // Form control stays compact
      this.selectedSources.setValue(next ? ['default'] : []);

      // Update tri-state & tags
      this.updateAllSourcesState();
      this.updateTagStates();
      return;
    }

    // Toggle just this source
    source.selected = !source.selected;

    // Recompute tri-state for "All Sources"
    this.updateAllSourcesState();

    if (this.allSourcesState === 'checked') {
      // Collapse to default when all selected
      this.selectedSources.setValue(['default']);
      // Ensure visual select-all
      this.sources.forEach(s => (s.selected = true));
    } else if (this.allSourcesState === 'unchecked') {
      this.selectedSources.setValue([]);
    } else {
      // Some selected
      const values = this.sources
          .filter(s => s.value !== 'default' && s.selected)
          .map(s => s.value);
      this.selectedSources.setValue(values);
    }

    this.updateTagStates();
  }

  onTagToggle(tag: { value: string; name: string; count: number }): void {
    const indices = this.tagToSourceIdx.get(tag.value) || [];
    // Toggle whole group: if NOT fully checked, make them all checked; otherwise uncheck all.
    const isFullyChecked = this.tagStates.get(tag.value) === 'checked';
    const next = !isFullyChecked;

    for (const i of indices) {
      // never toggle the synthetic "All Sources" row
      if (this.sources[i]?.value === 'default') continue;
      this.sources[i].selected = next;
    }

    // Push selection into the FormControl
    const nonDefaultSelected = this.sources.filter(s => s.value !== 'default' && s.selected).map(s => s.value);

    if (nonDefaultSelected.length === 0) {
      this.selectedSources.setValue([]);
    } else if (nonDefaultSelected.length === this.sources.length - 1) {
      // Collapse to default when all selected
      this.selectedSources.setValue(['default']);
      // Ensure visual select-all
      this.sources.forEach(s => (s.selected = true));
    } else {
      // Some selected
      this.selectedSources.setValue(nonDefaultSelected);
    }

    this.updateAllSourcesState();
    this.updateTagStates();
  }

  isSourceItem(item: DropItem): item is Extract<DropItem, { kind: 'source' }> { return item.kind === 'source'; }
  isTagItem(item: DropItem): item is Extract<DropItem, { kind: 'tag' }> { return item.kind === 'tag'; }
  getTagCount(tagValue: string): number { return this.spTags.find(t => t.value === tagValue)?.count ?? 0; }

  onDropdownItemToggle(item: DropItem): void {
    if (item.kind === 'source') {
      this.onSourceToggle(item.src);
    } else {
      const tag = this.spTags.find(t => t.value === item.tagValue);
      if (tag) this.onTagToggle(tag);
    }
  }

  private rebuildDropdownItems(): void {
    const sourceItems: Extract<DropItem, { kind: 'source' }>[] = this.sources.map(s => ({
      kind: 'source',
      key: `src:${s.value}`,
      label: s.name,
      src: s
    }));

    const tagItems: Extract<DropItem, { kind: 'tag' }>[] = this.spTags.map(t => ({
      kind: 'tag',
      key: `tag:${t.value}`,
      label: `#${t.name}`,
      tagValue: t.value
    }));

    const pinned = sourceItems.find(i => i.src.value === 'default');

    const rest: DropItem[] = [...sourceItems.filter(i => i !== pinned), ...tagItems]
        .sort((a, b) => a.label.replace(/^#/, '').localeCompare(b.label.replace(/^#/, ''), undefined, { sensitivity: 'base' }));

    this.dropdownItems = pinned ? [pinned, ...rest] : rest;
  }

  private updateTagStates(): void {
    this.tagStates.clear();

    for (const tag of this.spTags) {
      const idxs = (this.tagToSourceIdx.get(tag.value) || []).filter(i => this.sources[i]?.value !== 'default');
      const total = idxs.length;
      const selected = idxs.filter(i => this.sources[i].selected).length;

      let state: 'checked' | 'indeterminate' | 'unchecked' = 'unchecked';
      if (selected === 0) state = 'unchecked';
      else if (selected === total) state = 'checked';
      else state = 'indeterminate';

      this.tagStates.set(tag.value, state);
    }
  }

  private updateAllSourcesState(): void {
    const nonDefault = this.sources.filter(s => s.value !== 'default');
    const total = nonDefault.length;
    const selectedCount = nonDefault.filter(s => s.selected).length;

    if (selectedCount === 0) {
      this.allSourcesState = 'unchecked';
    } else if (selectedCount === total) {
      this.allSourcesState = 'checked';
    } else {
      this.allSourcesState = 'indeterminate';
    }

    // Reflect the visual state on the "All Sources" row:
    const allRow = this.sources.find(s => s.value === 'default');
    if (allRow) {
      // only show it as checked when truly all are selected
      allRow.selected = this.allSourcesState === 'checked';
    }
  }

  /**
   * Handles form submission to initiate a search.
   */
  onSubmit() {
    const currentQuery = this.searchForm.value?.query?.trim();

    if (currentQuery && currentQuery.length > 0) {
      // Prepare query parameters
      const queryParams: { [key: string]: any } = {
        q: currentQuery,
        timestamp: Date.now(), // Force navigation even if the query hasn't changed
      };

      // Check if non-default sources are selected
      if (!this.selectedSources.value.includes('default')) {
        const providers = this.selectedSources.value.join(',');
        queryParams['providers'] = providers;
      }

      if (this.searchForm.value.checkedGenerateAiResponse && this.isAIProviderExists) {
        queryParams['rag'] = 'true';
        
        // Preserve rag_timeout from current URL if present
        const currentRagTimeout = this.route.snapshot.queryParamMap.get('rag_timeout');
        if (currentRagTimeout) {
          queryParams['rag_timeout'] = currentRagTimeout;
        }
        
        // Only include the prompt if the prompts feature is enabled.
        if (this.authService.getPromptsStatus()) {
          const effectivePrompt = this.getEffectivePromptValue() || this.defaultPromptValue;
          queryParams['prompt'] = effectivePrompt;
          // Store the effective prompt for follow-up searches.
          this.currentRagPrompt = effectivePrompt;
  
          // Now handle AI instructions similarly.
          const instructions = this.searchForm.get('ai_instructions')?.value?.trim() || '';
          if (instructions.length > 0) {
            queryParams['ai_instructions'] = instructions;
            this.currentAiInstructions = instructions; // Persist the value for follow-up searches
          } else {
            delete queryParams['ai_instructions'];
            this.currentAiInstructions = '';
          }
        } else {
          // For Community edition, remove the prompt and instructions.
          delete queryParams['prompt'];
          delete queryParams['ai_instructions'];
          this.currentRagPrompt = '';
          this.currentAiInstructions = '';
        }
        
        // Clear previous AI metadata when starting a new RAG search
        this.searchService.clearRequestSpecs();
      }

      // Set the loading state to true
      this.setSearchLoadingState(true);

      // Update `previousParams` to reflect the latest search
      this.previousParams = {
        q: currentQuery,
        providers: queryParams['providers'] || '',
        rag: queryParams['rag'] || '',
        rag_timeout: queryParams['rag_timeout'] || '',
        prompt: queryParams['prompt'] || '',
      };

      // Navigate with updated query parameters (URL now includes a prompt param and, if provided, ai_instructions)
      this.router.navigate(['/'], { queryParams, state: { rerun: true } });
    }
  }

  /**
   * Handles changes to the "Generate AI Response" toggle.
   */
  toggleChange(event: Event) {
    if (!this.isAIProviderExists) {
      return;
    }

    const isChecked = (event.target as HTMLInputElement).checked;
    this.searchForm.get('checkedGenerateAiResponse')?.patchValue(isChecked);
  
    const queryParams = { ...this.route.snapshot.queryParams };

    if (!isChecked) {
      delete queryParams['rag'];
      delete queryParams['prompt'];
      delete queryParams['ai_instructions'];

      // Clear stored values when toggle is off.
      this.currentRagPrompt = '';
      this.currentAiInstructions = '';

      // Clear AI-related UI state
      this.searchService.clearRequestSpecs();

      // Clear the RAG loading state
      this.searchService.setRagLoading(false);

    } else {
      queryParams['rag'] = 'true';
      
      // Preserve rag_timeout from current URL if present
      const currentRagTimeout = this.route.snapshot.queryParamMap.get('rag_timeout');
      if (currentRagTimeout) {
        queryParams['rag_timeout'] = currentRagTimeout;
      }
      
      if (this.authService.getPromptsStatus()) {
        const prompt = this.getEffectivePromptValue() || this.defaultPromptValue;
        queryParams['prompt'] = prompt;
        // Store the prompt for follow-up searches.
        this.currentRagPrompt = prompt;
      } else {
        // Remove the prompt if prompts aren't supported.
        delete queryParams['prompt'];
        this.currentRagPrompt = '';
      }
  
      const providersIds = this.searchService.getRefine().map((r: any) => r['value']);
  
      // Process AI instructions:
      let instructions = this.searchForm.get('ai_instructions')?.value?.trim();
      if (instructions && instructions.length > 0) {
        queryParams['ai_instructions'] = instructions;
        this.currentAiInstructions = instructions;
      } else {
        delete queryParams['ai_instructions'];
        this.currentAiInstructions = '';
      }
      
      // Initiate the RAG search via Rest API
      this.searchService.setRagLoading(true);
      this.searchService.setRagShowing(true);
      this.searchService.setRagResults(null);
      this.searchService.clearRequestSpecs();
      this.setRagLoadingState(true);
      const restRequestStart = Date.now();

      // Get timeout from URL or use default from swirl service
      const ragTimeout = this.route.snapshot.queryParamMap.get('rag_timeout');
      const timeoutValue = ragTimeout ? parseInt(ragTimeout, 10) : this.swirl.timeout;

      this.swirl.getDetailSearchRag(
        this.searchContext.searchId,
        this.searchForm.get('query')?.value?.trim() || '',
        providersIds,
        this.currentRagPrompt,
        this.currentAiInstructions,
        this.searchService._selectedRagItems.value,
        timeoutValue
      ).subscribe({
        next: (response) => {
          const elapsed = Date.now() - restRequestStart;
          this.searchService.restResponseTime.next(elapsed);
          this.searchService.setRagResults(response);
          this.searchService.setRagLoading(false);
          this.setRagLoadingState(false)
          this.searchForm.get('checkedGenerateAiResponse')?.patchValue(false);
        },
        error: (err) => {
          const elapsed = Date.now() - restRequestStart;
          this.searchService.restResponseTime.next(elapsed);
          this.searchService.setRagLoading(false);
          this.setRagLoadingState(false);
          this.searchForm.get('checkedGenerateAiResponse')?.patchValue(false);
          
          // Handle timeout errors specifically
          let errorMessage = "An error occurred while fetching RAG data.";
          if (err.status === 504) {
            errorMessage = this.swirl.timeoutText;
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          this.searchService.setRagResults({"message": errorMessage});
        }
      });

    }
  
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: '',
    });
  }

  /**
   * Formats prompt names by replacing underscores with spaces and capitalizing words.
   */
  formatPromptName(name: string): string {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Resets the form to default values.
   */
  resetToDefault() {
    this.searchForm.reset({
      query: '',
      checkedGenerateAiResponse: false,
      selectedSources: ['default'],
      selectedPrompt: this.defaultPromptValue, // Reset to default prompt
      ai_instructions: '' // Clear instructions
    });

    if (!this.isAIProviderExists) {
      this.searchForm.get('checkedGenerateAiResponse')?.patchValue(false);
    }

    const queryParams: any = {
      q: '',
      providers: 'default',
      prompt: this.defaultPromptValue // Ensure default prompt is reflected in query parameters
    };

    this.router.navigate([], {
      queryParams: queryParams,
      replaceUrl: true
    });

    // this.isSearchButtonDisabled = false;
    this.isRAGButtonDisabled = false;

    // Also clear stored values.
    this.currentRagPrompt = '';
    this.currentAiInstructions = '';
  }
}
